import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { PulseLoader } from "react-spinners";
import useModuleAccess from "../hooks/useModuleAccess";
import { hasPermission } from "../Components/Permission";

const API_URL = process.env.REACT_APP_API_URL;

const TRACKER_STAT_CARDS = [
  {
    key: "totalCount",
    label: "Total Orders",
    titleColor: "#1F4E79",
    valueColor: "#2874A6",
    filterValue: null,
  },
  {
    key: "Acknowledged",
    label: "Acknowledged",
    titleColor: "#196F3D",
    valueColor: "#27AE60",
    filterValue: "Acknowledged",
  },
  {
    key: "Order on hold",
    label: "Order On Hold",
    titleColor: "#6C3483",
    valueColor: "#8E44AD",
    filterValue: "Order on Hold",
  },
  {
    key: "SPO Created",
    label: "SPO Created",
    titleColor: "#1A5276",
    valueColor: "#3498DB",
    filterValue: "SPO Created",
  },
  {
    key: "Dispatch in progress",
    label: "Dispatch In Progress",
    titleColor: "#7D6608",
    valueColor: "#D4AC0D",
    filterValue: "Dispatch in progress",
  },
      {
    key: "Partially Dispatched",
    label: "Partially Dispatched",
    titleColor: "#0E7490",
    valueColor: "#06B6D4",
    filterValue: "Partially Dispatched",
  },
    {
    key: "In Transit",
    label: "In Transit",
    titleColor: "#3730A3",
    valueColor: "#6366F1",
    filterValue: "In Transit",
  },
  {
    key: "Dispatched",
    label: "Dispatched",
    titleColor: "#196F3D",
    valueColor: "#2ECC71",
    filterValue: "Dispatched",
  },
  {
    key: "Delivered",
    label: "Delivered",
    titleColor: "#154360",
    valueColor: "#2874A6",
    filterValue: "Delivered",
  },
  {
    key: "Replacement in progress",
    label: "Replacement In Progress",
    titleColor: "#884EA0",
    valueColor: "#AF7AC5",
    filterValue: "Replacement in progress",
  },
  {
    key: "GRN Completed",
    label: "GRN Completed",
    titleColor: "#145A32",
    valueColor: "#229954",
    filterValue: "GRN Completed",
  },
  {
    key: "Payment completed",
    label: "Payment Completed",
    titleColor: "#7D6608",
    valueColor: "#F4D03F",
    filterValue: "Payment completed",
  },
  {
    key: "Draft",
    label: "Draft",
    titleColor: "#424949",
    valueColor: "#7F8C8D",
    filterValue: "Draft",
  },
  {
    key: "Order Closed",
    label: "Order Closed",
    titleColor: "#424949",
    valueColor: "#566573",
    filterValue: "Order Closed",
  },
  {
    key: "Internal issue",
    label: "Internal Issue",
    titleColor: "#922B21",
    valueColor: "#E74C3C",
    filterValue: "Internal issue",
  },
];
const DEFAULT_TRACKER_STATS = TRACKER_STAT_CARDS.reduce(
  (acc, card) => ({
    ...acc,
    [card.key]: 0,
  }),
  {},
);

const TrackerStatCardSkeleton = () => (
  <div className="tracker-stat-col">
    <div className="card supplier-stat-card">
      <div className="card-body">
        <div className="stat-skeleton stat-skeleton-title"></div>
        <div className="stat-skeleton stat-skeleton-value"></div>
      </div>
    </div>
  </div>
);

const TrackerStatCard = ({
  label,
  value,
  titleColor,
  valueColor,
  onClick,
  isActive,
}) => (
  <div className="tracker-stat-col">
    <div
      className={`card supplier-stat-card procurement-stat-card-clickable${
        isActive ? " procurement-stat-card-active" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      role="button"
      tabIndex={0}
      title={`Filter by ${label}`}
    >
      <div className="card-body">
        <div className="stat-title" style={{ color: titleColor }}>
          {label}
        </div>

        <div className="stat-value" style={{ color: valueColor }}>
          {value}
        </div>
      </div>
    </div>
  </div>
);

const PO_STATUS_OPTIONS = [
  "Draft",
  "Internal issue",
  "Acknowledged",
  "SPO Created",
  "Dispatch in progress",
  "Partially Dispatched",
  "Dispatched",
  "In Transit", 
  "Delivered",
  "Replacement in progress",
  "GRN Completed",
  "Payment completed",
  "Order on Hold",
  "Order Closed",
];

const PO_STATUS_COLORS = {
  Draft: "#6B7280",
  "Internal issue": "#B45309",
  Acknowledged: "#2563EB",
  "SPO Created": "#7C3AED",
  "Dispatch in progress": "#0891B2",
  "Partially Dispatched": "#0E7490",
  Dispatched: "#0F766E",
  "In Transit": "#4F46E5", 
  Delivered: "#059669",
  "Replacement in progress": "#D97706",
  "GRN Completed": "#0EA5E9",
  "Payment completed": "#BE185D",
  "Order on Hold": "#CA8A04",
  "Order Closed": "#374151",
};

const PO_STATUS_ALIASES = {
  Acknwoledged: "Acknowledged",
  "Dispached in progress": "Dispatch in progress",
  "Dispatched in progress": "Dispatch in progress",
  "Replacemnt in progress": "Replacement in progress",
  "Order on hold": "Order on Hold",
  "Order closed": "Order Closed",
  "Partially dispatched": "Partially Dispatched",  
  "In transit": "In Transit", 
};

function toApiDDMMYYYY(isoDate) {
  if (!isoDate) return "";
  const [yyyy, mm, dd] = isoDate.split("-");
  if (!yyyy || !mm || !dd) return "";
  return `${dd}-${mm}-${yyyy}`;
}

function unwrapApiBody(raw) {
  let responseData = raw;
  if (typeof responseData === "string") {
    try {
      responseData = JSON.parse(responseData);
    } catch {
      return raw;
    }
  }
  if (responseData?.body != null) {
    if (typeof responseData.body === "string") {
      try {
        return JSON.parse(responseData.body);
      } catch {
        return responseData.body;
      }
    }
    return responseData.body;
  }
  return responseData;
}

function extractExcelBase64(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t.startsWith("UEs")) return raw;
    if (t.startsWith("{")) {
      try {
        return extractExcelBase64(JSON.parse(t));
      } catch {
        return null;
      }
    }
    return null;
  }
  if (typeof raw === "object") {
    if (raw.body != null) return extractExcelBase64(raw.body);
    if (typeof raw.data === "string") return extractExcelBase64(raw.data);
    if (raw.data != null && typeof raw.data === "object") {
      const nested = extractExcelBase64(raw.data);
      if (nested) return nested;
    }
    if (raw.file != null) return extractExcelBase64(raw.file);
    if (raw.base64 != null) return extractExcelBase64(raw.base64);
  }
  return null;
}

function triggerXlsxDownloadFromBase64(base64) {
  if (!base64 || typeof base64 !== "string" || !base64.startsWith("UEs")) {
    throw new Error("Invalid Excel data received");
  }
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
  link.download = `tracker-export-${timestamp}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

function formatTrackerAmount(value) {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (!Number.isFinite(num)) return String(value);
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: Number.isInteger(num) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

const DEFAULT_FILTER_CHECKS = {
  customer: false,
  user: false,
  status: false,
  dateRange: false,
};
const DEFAULT_FILTER_INPUTS = {
  customer: "",
  user: "",
  status: "",
  fromDate: "",
  toDate: "",
};
const DEFAULT_ACTIVE_MULTI_FILTERS = {
  customer: "",
  user: "",
  status: "",
  fromDate: "",
  toDate: "",
};
const MAX_ACTIVE_FILTERS = 2; 

const TrackerUpdate = () => {
  const { canCreate, canView, canUpdate, user } =
    useModuleAccess("tracker_update");
  const savedFilters = JSON.parse(
    sessionStorage.getItem("trackerFilters") || "{}",
  );
  const navigate = useNavigate();
  const userRole = String(user?.role || localStorage.getItem("role") || "")
    .trim()
    .toLowerCase();
  const userPermissions = Array.isArray(user?.permissions)
    ? user.permissions
    : [];
  // Flag permissions: role menu keys (not moduleAccess CRUD)
  const canImport =
    userRole === "admin" ||
    userPermissions.includes("tracker_update_import") ||
    hasPermission("tracker_update_import");
  const canExport =
    userRole === "admin" ||
    userPermissions.includes("tracker_update_export") ||
    hasPermission("tracker_update_export");
  const [trackers, setTrackers] = useState([]);
  const [filteredTrackers, setFilteredTrackers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(
    savedFilters.searchQuery || "",
  );
  const [activeSearchQuery, setActiveSearchQuery] = useState(
    savedFilters.activeSearchQuery || "",
  );
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(savedFilters.currentPage || 1);
  const [itemsPerPage, setItemsPerPage] = useState(
    savedFilters.itemsPerPage || 25,
  );

  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  const [filterChecks, setFilterChecks] = useState({
    ...DEFAULT_FILTER_CHECKS,
    ...(savedFilters.filterChecks || {}),
  });
  const [filterInputs, setFilterInputs] = useState({
    ...DEFAULT_FILTER_INPUTS,
    ...(savedFilters.filterInputs || {}),
  });
  const [activeMultiFilters, setActiveMultiFilters] = useState({
    ...DEFAULT_ACTIVE_MULTI_FILTERS,
    ...(savedFilters.activeMultiFilters || {}),
  });
  const filterPanelRef = useRef(null);

  const [customerFilterOptions, setCustomerFilterOptions] = useState([]);
  const [userFilterOptions, setUserFilterOptions] = useState([]);
  const [isPreloadingFilterOptions, setIsPreloadingFilterOptions] =
    useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportMode, setExportMode] = useState("all");
  const [exportFromDate, setExportFromDate] = useState("");
  const [exportToDate, setExportToDate] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [trackerStats, setTrackerStats] = useState({
    ...DEFAULT_TRACKER_STATS,
  });

  const [isTrackerCountLoading, setIsTrackerCountLoading] = useState(true);

  const anyMultiFilterActive =
    !!activeMultiFilters.customer ||
    !!activeMultiFilters.user ||
    !!activeMultiFilters.status ||
    !!activeMultiFilters.fromDate ||
    !!activeMultiFilters.toDate;

  const columnMap = {
    poNo: "poNo",
    poDate: "poDate",
    customerName: "customerName",
    materialCode: "materialCode",
    itemDesc: "itemDesc",
    quantityOrdered: "quantityOrdered",
    quantityDispatched: "quantityDispatched",
    poStatus: "poStatus",
    updatedBy: "updatedBy",
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        filterPanelRef.current &&
        !filterPanelRef.current.contains(e.target)
      ) {
        setIsFilterPanelOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSort = (column) => {
    const apiField = columnMap[column] || column;

    if (sortBy === apiField) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortBy("createdAt");
        setSortOrder("desc");
      }
    } else {
      setSortBy(apiField);
      setSortOrder("asc");
    }

    setCurrentPage(1);
  };

  const getSortIcon = (column) => {
    const apiField = columnMap[column] || column;

    if (sortBy !== apiField) {
      return (
        <i
          className="fa-solid fa-sort text-muted ml-1"
          style={{ fontSize: "0.8em" }}
        />
      );
    }

    if (sortOrder === "asc") {
      return (
        <i
          className="fa-solid fa-sort-up text-primary ml-1"
          style={{ fontSize: "0.8em" }}
        />
      );
    }

    return (
      <i
        className="fa-solid fa-sort-down text-primary ml-1"
        style={{ fontSize: "0.8em" }}
      />
    );
  };

  // Ref to prevent duplicate toasts
  const lastToastRef = useRef({ message: "", timestamp: 0 });

  const parseTrackerResponseData = (raw) => {
    let responseData = raw;
    if (typeof responseData === "string") {
      try {
        responseData = JSON.parse(responseData);
      } catch {
        responseData = raw;
      }
    }
    if (responseData?.body) {
      if (typeof responseData.body === "string") {
        try {
          responseData = JSON.parse(responseData.body);
        } catch {
          responseData = responseData.body;
        }
      } else {
        responseData = responseData.body;
      }
    }
    return responseData;
  };

  const getTrackersArray = (responseData) =>
    responseData.trackers ||
    responseData.data?.trackers ||
    responseData.data?.data ||
    [];

  const getFilterOptionLabel = (type, value) => {
    if (!value) return "";
    const options =
      type === "customer"
        ? customerFilterOptions
        : type === "user"
          ? userFilterOptions
          : [];
    return options.find((option) => option.value === value)?.label || value;
  };

  const applyTrackerFiltersToPayload = useCallback(
    (payload, { forCount = false } = {}) => {
      if (activeMultiFilters.customer) {
        payload.customerId = activeMultiFilters.customer;
      }
      if (activeMultiFilters.user) {
        payload.updatedBy = activeMultiFilters.user;
      }
      if (activeMultiFilters.status && !forCount) {
        payload.poStatus = activeMultiFilters.status;
      }
      if (activeMultiFilters.fromDate) {
        payload.fromDate = toApiDDMMYYYY(activeMultiFilters.fromDate);
      }
      if (activeMultiFilters.toDate) {
        payload.toDate = toApiDDMMYYYY(activeMultiFilters.toDate);
      }
      return payload;
    },
    [activeMultiFilters],
  );

  const preloadFilterOptions = useCallback(async () => {
    setIsPreloadingFilterOptions(true);
    try {
      const [customersRes, usersRes] = await Promise.all([
        axios.post(API_URL, { action: "get-all-customers", search: "" }),
        axios.post(API_URL, { action: "get-tracker-users" }),
      ]);

      const customersData = parseTrackerResponseData(customersRes.data);
      const customersList =
        customersData?.data?.customers ||
        customersData?.customers ||
        customersData?.data?.data ||
        (Array.isArray(customersData?.data) ? customersData.data : []) ||
        [];
      setCustomerFilterOptions(
        (Array.isArray(customersList) ? customersList : [])
          .map((item) => ({
            value: String(item?._id || item?.id || "").trim(),
            label: String(
              item?.customerName || item?.customer || item?.name || "",
            ).trim(),
          }))
          .filter((item) => item.value && item.label)
          .sort((a, b) => a.label.localeCompare(b.label)),
      );

      const usersData = parseTrackerResponseData(usersRes.data);
      const usersList = Array.isArray(usersData?.data)
        ? usersData.data
        : usersData?.users || usersData?.data?.users || [];
      setUserFilterOptions(
        (Array.isArray(usersList) ? usersList : [])
          .map((item) => ({
            value: String(item?._id || item?.id || "").trim(),
            label: String(
              item?.name || item?.userName || item?.email || "",
            ).trim(),
          }))
          .filter((item) => item.value && item.label)
          .sort((a, b) => a.label.localeCompare(b.label)),
      );
    } catch (error) {
      console.error("Preload filter options error:", error);
      toast.error("Failed to load filter options", { autoClose: 2000 });
      setCustomerFilterOptions([]);
      setUserFilterOptions([]);
    } finally {
      setIsPreloadingFilterOptions(false);
    }
  }, []);

  const isCustomerOptionsLoading =
    isPreloadingFilterOptions && customerFilterOptions.length === 0;
  const isUserOptionsLoading =
    isPreloadingFilterOptions && userFilterOptions.length === 0;

  // Fetch trackers with server-side pagination
  const fetchTrackers = useCallback(async () => {
    const userId = localStorage.getItem("id");
    if (!userId) {
      toast.error("User session expired. Please login again.", {
        autoClose: 2000,
      });
      setTimeout(() => {
        navigate("/");
      }, 2000);
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        action: "get-trackers-internal",
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
      };

      // Add search parameter if activeSearchQuery has a value
      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      applyTrackerFiltersToPayload(payload);

      const response = await axios.post(API_URL, payload);

      const responseData = parseTrackerResponseData(response.data);

      // Log response for debugging
      console.log("Tracker API Response:", responseData);

      // Check if request was successful
      if (responseData.success === true) {
        // Try to get trackers from different possible locations
        const trackersArray = getTrackersArray(responseData);

        setTrackers(trackersArray);
        setFilteredTrackers(trackersArray);

        // Use pagination from data object if available, otherwise from pagination object, or fallback
        if (responseData.data && responseData.data.totalCount !== undefined) {
          setTotalCount(responseData.data.totalCount);
        } else if (
          responseData.pagination &&
          responseData.pagination.totalCount
        ) {
          setTotalCount(responseData.pagination.totalCount);
        } else {
          setTotalCount(responseData.count || trackersArray.length);
        }

        // Show success toast only if there's a message and we haven't shown it recently
        if (responseData.message) {
          const now = Date.now();
          const timeSinceLastToast = now - lastToastRef.current.timestamp;
          const isSameMessage =
            lastToastRef.current.message === responseData.message;

          // Only show toast if it's a different message or more than 1 second has passed
          if (!isSameMessage || timeSinceLastToast > 1000) {
            lastToastRef.current = {
              message: responseData.message,
              timestamp: now,
            };
            // toast.success(responseData.message, { autoClose: 2000 });
          }
        }
      } else {
        toast.error(responseData.message || "Failed to fetch trackers", {
          autoClose: 2000,
        });
        setTrackers([]);
        setFilteredTrackers([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error("Fetch trackers error:", error);
      if (error.response && error.response.data) {
        let errorData = error.response.data;
        if (typeof errorData === "string") {
          try {
            errorData = JSON.parse(errorData);
          } catch (e) {
            // Use error message as is
          }
        }
        toast.error(
          errorData.message || "An error occurred while fetching trackers",
          { autoClose: 2000 },
        );
      } else {
        toast.error(
          "Network error. Please check your connection and try again.",
          { autoClose: 2000 },
        );
      }
      setTrackers([]);
      setFilteredTrackers([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeSearchQuery,
    applyTrackerFiltersToPayload,
    sortBy,
    sortOrder,
    navigate,
  ]);

  useEffect(() => {
    fetchTrackers();
  }, [fetchTrackers]);

  useEffect(() => {
    preloadFilterOptions();
  }, [preloadFilterOptions]);

  useEffect(() => {
    const filters = {
      searchQuery,
      activeSearchQuery,
      currentPage,
      itemsPerPage,
      filterChecks,
      filterInputs,
      activeMultiFilters,
    };
    sessionStorage.setItem("trackerFilters", JSON.stringify(filters));
  }, [
    searchQuery,
    activeSearchQuery,
    currentPage,
    itemsPerPage,
    filterChecks,
    filterInputs,
    activeMultiFilters,
  ]);

  const isTrackerStatCardActive = (card) => {
    if (!card.filterValue) {
      return !activeMultiFilters.status;
    }
    return activeMultiFilters.status === card.filterValue;
  };

  const handleTrackerStatCardClick = (card) => {
    if (!card.filterValue) {
      setFilterChecks((prev) => ({ ...prev, status: false }));
      setFilterInputs((prev) => ({ ...prev, status: "" }));
      setActiveMultiFilters((prev) => ({ ...prev, status: "" }));
      if (currentPage !== 1) setCurrentPage(1);
      return;
    }

    if (!filterChecks.status) {
      const otherActiveKeys = Object.keys(filterChecks).filter(
        (key) => key !== "status" && filterChecks[key],
      );

      if (otherActiveKeys.length >= MAX_ACTIVE_FILTERS) {
        const keyToRemove = otherActiveKeys[0];
        const clearRemovedInputs =
          keyToRemove === "dateRange"
            ? { fromDate: "", toDate: "" }
            : { [keyToRemove]: "" };
        setFilterChecks((prev) => ({
          ...prev,
          [keyToRemove]: false,
          status: true,
        }));
        setFilterInputs((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          status: card.filterValue,
        }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          ...clearRemovedInputs,
          status: card.filterValue,
        }));
        toast.info("Replaced an existing filter to apply Status", {
          autoClose: 2000,
        });
      } else {
        setFilterChecks((prev) => ({ ...prev, status: true }));
        setFilterInputs((prev) => ({ ...prev, status: card.filterValue }));
        setActiveMultiFilters((prev) => ({
          ...prev,
          status: card.filterValue,
        }));
      }
    } else {
      setFilterInputs((prev) => ({ ...prev, status: card.filterValue }));
      setActiveMultiFilters((prev) => ({ ...prev, status: card.filterValue }));
    }

    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const fetchTrackerStatusCount = useCallback(async () => {
    try {
      setIsTrackerCountLoading(true);

      const payload = applyTrackerFiltersToPayload(
        { action: "count-trackers" },
        { forCount: true },
      );

      const response = await axios.post(API_URL, payload);

      let data = response.data;

      if (typeof data === "string") {
        data = JSON.parse(data);
      }

      if (data.success) {
        setTrackerStats({
          ...DEFAULT_TRACKER_STATS,
          ...data.data,
        });
      }
    } catch (error) {
      console.error("Tracker count error:", error);
    } finally {
      setIsTrackerCountLoading(false);
    }
  }, [applyTrackerFiltersToPayload]);

  useEffect(() => {
    fetchTrackerStatusCount();
  }, [fetchTrackerStatusCount]);

  // Calculate total pages
  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage) || 1);
  const startItem =
    trackers.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalCount);

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);

      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }, 100);
    }
  };

  // Handle items per page change
  const handleItemsPerPageChange = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 7; // Show up to 7 page numbers

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show pages with ellipsis
      if (currentPage <= 4) {
        // Show first pages
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        // Show last pages
        pages.push(1);
        pages.push("ellipsis");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Show middle pages
        pages.push(1);
        pages.push("ellipsis");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  // Handle search input change - if field becomes empty, automatically reset search
  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearchQuery(newValue);

    // If user backspaces and field becomes empty, automatically reset search to show all records
    const trimmedValue = newValue ? newValue.trim() : "";
    if (trimmedValue === "" && activeSearchQuery !== "") {
      // Field is now empty and there was an active search, so reset it
      setActiveSearchQuery("");
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  };

  // Handle search trigger - on Enter key or search icon click
  const handleSearchTrigger = () => {
    const trimmedQuery = searchQuery ? searchQuery.trim() : "";

    // If search query is empty (user backspaced all characters), reset the active search
    if (trimmedQuery === "") {
      setActiveSearchQuery("");
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    } else {
      setActiveSearchQuery(trimmedQuery);
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  };

  // Handle Enter key press in search input
  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearchTrigger();
    }
  };

  const handleFilterCheckToggle = (key) => {
    setFilterChecks((prev) => {
      const isCurrentlyChecked = prev[key];

      if (!isCurrentlyChecked) {
        const activeCount = Object.values(prev).filter(Boolean).length;
        if (activeCount >= MAX_ACTIVE_FILTERS) {
          toast.error(
            `You can select up to ${MAX_ACTIVE_FILTERS} filters only`,
            {
              autoClose: 2000,
            },
          );
          return prev;
        }
      }

      const next = { ...prev, [key]: !isCurrentlyChecked };

      if (!next[key]) {
        if (key === "dateRange") {
          setFilterInputs((inputs) => ({
            ...inputs,
            fromDate: "",
            toDate: "",
          }));
          setActiveMultiFilters((active) => ({
            ...active,
            fromDate: "",
            toDate: "",
          }));
        } else {
          setFilterInputs((inputs) => ({ ...inputs, [key]: "" }));
          setActiveMultiFilters((active) => ({ ...active, [key]: "" }));
        }
        if (currentPage !== 1) {
          setCurrentPage(1);
        }
      }

      return next;
    });
  };

  const handleFilterInputChange = (key, value) => {
    if (key === "fromDate" || key === "toDate") {
      const nextFrom = key === "fromDate" ? value : filterInputs.fromDate;
      const nextTo = key === "toDate" ? value : filterInputs.toDate;

      if (nextFrom && nextTo && new Date(nextFrom) > new Date(nextTo)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }

      setFilterInputs((prev) => ({ ...prev, [key]: value }));
      setActiveMultiFilters((prev) => ({
        ...prev,
        fromDate: nextFrom,
        toDate: nextTo,
      }));
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      return;
    }

    setFilterInputs((prev) => ({ ...prev, [key]: value }));
    setActiveMultiFilters((prev) => ({ ...prev, [key]: value }));
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const handleClearFilter = () => {
    setFilterChecks(DEFAULT_FILTER_CHECKS);
    setFilterInputs(DEFAULT_FILTER_INPUTS);
    setActiveMultiFilters(DEFAULT_ACTIVE_MULTI_FILTERS);
    setIsFilterPanelOpen(false);
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  const getPoStatusColor = (status) => {
    if (!status) return "#6B7280";
    const normalized = PO_STATUS_ALIASES[status] || status;
    return PO_STATUS_COLORS[normalized] || "#6B7280";
  };

  const handleExport = async () => {
    if (exportMode === "date") {
      if (!exportFromDate || !exportToDate) {
        toast.error("Please select both from date and to date", {
          autoClose: 2000,
        });
        return;
      }
      if (new Date(exportFromDate) > new Date(exportToDate)) {
        toast.error("From date cannot be greater than to date", {
          autoClose: 2000,
        });
        return;
      }
    }

    setIsExporting(true);

    try {
      const payload = {
        action: "export-trackers",
      };

      const trimmedSearch = activeSearchQuery ? activeSearchQuery.trim() : "";
      if (trimmedSearch) {
        payload.search = trimmedSearch;
      }

      applyTrackerFiltersToPayload(payload);

      if (exportMode === "date") {
        payload.fromDate = toApiDDMMYYYY(exportFromDate);
        payload.toDate = toApiDDMMYYYY(exportToDate);
      }

      const response = await axios.post(API_URL, payload, {
        responseType: "json",
      });

      let base64 = extractExcelBase64(unwrapApiBody(response.data));
      if (!base64 && response.data?.body) {
        base64 =
          typeof response.data.body === "string" &&
          response.data.body.trim().startsWith("UEs")
            ? response.data.body
            : extractExcelBase64(response.data.body);
      }
      if (!base64) {
        base64 = extractExcelBase64(response.data);
      }

      triggerXlsxDownloadFromBase64(base64);
      toast.success("Export completed successfully", { autoClose: 2000 });
      setShowExportModal(false);
    } catch (error) {
      console.error("Export trackers error:", error);
      const errMsg =
        unwrapApiBody(error.response?.data)?.message ||
        error.message ||
        "Failed to export data. Please try again.";
      toast.error(errMsg, { autoClose: 2000 });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1 className="m-0 txt_lft">Customer Order Report</h1>
            </div>
            <div className="col-sm-6 txt_rgt">
              {canCreate && (
                <Link to="/trackerAdd" className="btn btn-blue mr-2">
                  Add
                </Link>
              )}
              {canImport && (
                <Link to="/trackerUpdateAdd" className="btn btn-blue mr-2">
                  Import
                </Link>
              )}
              {canExport && (
                <button
                  type="button"
                  className="btn btn-blue"
                  onClick={() => setShowExportModal(true)}
                  disabled={isExporting}
                >
                  <i
                    className={`fa-solid ${isExporting ? "fa-spinner fa-spin" : "fa-file-export"} mr-1`}
                  />
                  {isExporting ? "Exporting..." : "Export"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="tracker-stat-card-row">
                {isTrackerCountLoading
                  ? TRACKER_STAT_CARDS.map((card) => (
                      <TrackerStatCardSkeleton key={card.key} />
                    ))
                  : TRACKER_STAT_CARDS.map((card) => (
                      <TrackerStatCard
                        key={card.key}
                        label={card.label}
                        value={trackerStats[card.key]}
                        titleColor={card.titleColor}
                        valueColor={card.valueColor}
                        isActive={isTrackerStatCardActive(card)}
                        onClick={() => handleTrackerStatCardClick(card)}
                      />
                    ))}
              </div>
              <div className="card-body">
                <div className="row align-items-end mb-3 flex-xl-nowrap">
                  <div className="col-12 col-sm-6 col-md-4 col-xl-3 mb-2 mb-xl-0">
                    <div className="form-group mb-0">
                      <label
                        className="mb-1"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Search
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search trackers..."
                          value={searchQuery}
                          onChange={handleSearchChange}
                          onKeyPress={handleSearchKeyPress}
                        />
                        <div className="input-group-append">
                          <div
                            className="input-group-text crsr_pntr"
                            onClick={handleSearchTrigger}
                            style={{ cursor: "pointer" }}
                          >
                            <i className="fa-solid fa-search"></i>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                    <div
                      className="form-group mb-0 position-relative"
                      ref={filterPanelRef}
                    >
                      <label
                        className="mb-1"
                        style={{ fontSize: "13px", fontWeight: "500" }}
                      >
                        Filter
                      </label>
                      <button
                        type="button"
                        className="form-control text-left d-flex justify-content-between align-items-center"
                        onClick={() => setIsFilterPanelOpen((open) => !open)}
                      >
                        <span
                          style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {anyMultiFilterActive
                            ? "Filters applied"
                            : "Select Filters"}
                        </span>
                        <i
                          className={`fa-solid fa-chevron-${isFilterPanelOpen ? "up" : "down"}`}
                          style={{
                            fontSize: "11px",
                            flexShrink: 0,
                            marginLeft: "6px",
                          }}
                        ></i>
                      </button>
                      {isFilterPanelOpen && (
                        <div
                          className="tracker-filter-dropdown"
                          style={{
                            position: "absolute",
                            zIndex: 20,
                            top: "100%",
                            left: 0,
                            marginTop: "4px",
                            background: "#fff",
                            border: "1px solid #ced4da",
                            borderRadius: "4px",
                            padding: "10px 12px",
                            width: "200px",
                            maxWidth: "calc(100vw - 32px)",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.15)",
                          }}
                        >
                          <div className="custom-control custom-checkbox mb-2">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="filterCheckCustomer"
                              checked={filterChecks.customer}
                              disabled={
                                !filterChecks.customer &&
                                Object.values(filterChecks).filter(Boolean)
                                  .length >= MAX_ACTIVE_FILTERS
                              }
                              onChange={() =>
                                handleFilterCheckToggle("customer")
                              }
                            />
                            <label
                              className="custom-control-label"
                              htmlFor="filterCheckCustomer"
                            >
                              Customer
                            </label>
                          </div>
                          <div className="custom-control custom-checkbox mb-2">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="filterCheckUser"
                              checked={filterChecks.user}
                              disabled={
                                !filterChecks.user &&
                                Object.values(filterChecks).filter(Boolean)
                                  .length >= MAX_ACTIVE_FILTERS
                              }
                              onChange={() => handleFilterCheckToggle("user")}
                            />
                            <label
                              className="custom-control-label"
                              htmlFor="filterCheckUser"
                            >
                              User
                            </label>
                          </div>
                          <div className="custom-control custom-checkbox mb-2">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="filterCheckStatus"
                              checked={filterChecks.status}
                              disabled={
                                !filterChecks.status &&
                                Object.values(filterChecks).filter(Boolean)
                                  .length >= MAX_ACTIVE_FILTERS
                              }
                              onChange={() => handleFilterCheckToggle("status")}
                            />
                            <label
                              className="custom-control-label"
                              htmlFor="filterCheckStatus"
                            >
                              Status
                            </label>
                          </div>
                          <div className="custom-control custom-checkbox">
                            <input
                              type="checkbox"
                              className="custom-control-input"
                              id="filterCheckDateRange"
                              checked={!!filterChecks.dateRange}
                              disabled={
                                !filterChecks.dateRange &&
                                Object.values(filterChecks).filter(Boolean)
                                  .length >= MAX_ACTIVE_FILTERS
                              }
                              onChange={() =>
                                handleFilterCheckToggle("dateRange")
                              }
                            />
                            <label
                              className="custom-control-label"
                              htmlFor="filterCheckDateRange"
                            >
                              Date
                            </label>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {filterChecks.customer && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Customer
                        </label>
                        <select
                          className="form-control"
                          value={filterInputs.customer}
                          onChange={(e) =>
                            handleFilterInputChange("customer", e.target.value)
                          }
                          disabled={isCustomerOptionsLoading}
                        >
                          <option value="">
                            {isCustomerOptionsLoading
                              ? "Loading..."
                              : "Select Customer"}
                          </option>
                          {customerFilterOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.user && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          User
                        </label>
                        <select
                          className="form-control"
                          value={filterInputs.user}
                          onChange={(e) =>
                            handleFilterInputChange("user", e.target.value)
                          }
                          disabled={isUserOptionsLoading}
                        >
                          <option value="">
                            {isUserOptionsLoading
                              ? "Loading..."
                              : "Select User"}
                          </option>
                          {userFilterOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.status && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                      <div className="form-group mb-0">
                        <label
                          className="mb-1"
                          style={{ fontSize: "13px", fontWeight: "500" }}
                        >
                          Status
                        </label>
                        <select
                          className="form-control"
                          value={filterInputs.status}
                          onChange={(e) =>
                            handleFilterInputChange("status", e.target.value)
                          }
                        >
                          <option value="">Select Status</option>
                          {PO_STATUS_OPTIONS.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {filterChecks.dateRange && (
                    <>
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            From Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={filterInputs.fromDate || ""}
                            max={filterInputs.toDate || undefined}
                            onChange={(e) =>
                              handleFilterInputChange(
                                "fromDate",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>
                      <div className="col-12 col-sm-6 col-md-4 col-xl-2 mb-2 mb-xl-0">
                        <div className="form-group mb-0">
                          <label
                            className="mb-1"
                            style={{ fontSize: "13px", fontWeight: "500" }}
                          >
                            To Date
                          </label>
                          <input
                            type="date"
                            className="form-control"
                            value={filterInputs.toDate || ""}
                            min={filterInputs.fromDate || undefined}
                            onChange={(e) =>
                              handleFilterInputChange("toDate", e.target.value)
                            }
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {anyMultiFilterActive && (
                    <div className="col-12 col-sm-6 col-md-4 col-xl-auto mb-2 mb-xl-0">
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm w-100"
                        onClick={handleClearFilter}
                      >
                        <i className="fa-solid fa-times mr-1"></i>
                        Clear Filter
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {isLoading ? (
                <div className="text-center p-4">
                  <PulseLoader color="#1199B2" size={10} />
                </div>
              ) : trackers.length === 0 ? (
                <div className="nocontent_div text-center p-4">
                  There are no trackers yet.
                  <br />
                  {/* <div className="mt-3">
                    <button className="btn btn-blue">Add Tracker</button>
                  </div> */}
                </div>
              ) : filteredTrackers.length === 0 && activeSearchQuery ? (
                <div className="nocontent_div text-center p-4">
                  No trackers found matching your search.
                  <br />
                  <div className="mt-3">
                    <button
                      className="btn btn-blue"
                      onClick={() => {
                        setSearchQuery("");
                        setActiveSearchQuery("");
                        setCurrentPage(1);
                      }}
                    >
                      Clear Search
                    </button>
                  </div>
                </div>
              ) : (
                <div className="supply-table-container">
                  <div className="table-responsive">
                    <table
                      id="trackers"
                      width="100%"
                      className="table table-striped table-bordered txt_lft"
                    >
                      <thead>
                        <tr style={{ fontSize: "12px" }}>
                          <th
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            S.No
                          </th>
                          <th
                            onClick={() => handleSort("poNo")}
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            {" "}
                            PO No {getSortIcon("poNo")}
                          </th>
                          <th
                            onClick={() => handleSort("poDate")}
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            {" "}
                            PO Date {getSortIcon("poDate")}
                          </th>
                          <th
                            onClick={() => handleSort("customerName")}
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            Customer Name {getSortIcon("customerName")}
                          </th>
                          <th
                            onClick={() => handleSort("materialCode")}
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            {" "}
                            Material Code {getSortIcon("materialCode")}
                          </th>
                          <th
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            Material Description
                          </th>
                          <th
                            onClick={() => handleSort("quantityOrdered")}
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            {" "}
                            PO Quantity {getSortIcon("quantityOrdered")}
                          </th>
                          <th
                            onClick={() => handleSort("quantityDispatched")}
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            {" "}
                            Quantity Dispatched{" "}
                            {getSortIcon("quantityDispatched")}
                          </th>
                          <th
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            CPO
                          </th>
                          <th
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            SPO
                          </th>
                          <th
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            Total Margin
                          </th>
                          <th
                            onClick={() => handleSort("poStatus")}
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            {" "}
                            PO Status {getSortIcon("poStatus")}
                          </th>
                          <th
                            onClick={() => handleSort("updatedBy")}
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            {" "}
                            Last Updated By {getSortIcon("updatedBy")}
                          </th>
                          <th
                            style={{ whiteSpace: "nowrap", fontSize: "12px" }}
                          >
                            Last Updated Date
                          </th>
                          <th style={{ fontSize: "12px" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTrackers.map((tracker, index) => (
                          <tr
                            key={tracker._id || tracker.id || index}
                            style={{ fontSize: "12px" }}
                          >
                            <td style={{ fontSize: "12px" }}>
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {tracker.poNo || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {tracker.poDate
                                ? new Date(tracker.poDate).toLocaleDateString()
                                : "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {tracker.customerName || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {tracker.materialCode || "-"}
                            </td>
                            <td
                              style={{
                                fontSize: "12px",
                                maxWidth: "300px",
                                whiteSpace: "normal",
                                wordWrap: "break-word",
                              }}
                            >
                              {tracker.itemDesc || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {tracker.quantityOrdered || "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {tracker.quantityDispatched != null &&
                              tracker.quantityDispatched !== ""
                                ? tracker.quantityDispatched
                                : "-"}
                            </td>
                            <td
                              style={{ fontSize: "12px", whiteSpace: "nowrap" }}
                            >
                              {formatTrackerAmount(tracker.cpoBasicPrice)}
                            </td>
                            <td
                              style={{ fontSize: "12px", whiteSpace: "nowrap" }}
                            >
                              {formatTrackerAmount(tracker.spoBasicPrice)}
                            </td>
                            <td
                              style={{ fontSize: "12px", whiteSpace: "nowrap" }}
                            >
                              {(() => {
                                const cpo = Number(tracker.cpoBasicPrice);
                                const spo = Number(tracker.spoBasicPrice);
                                const qty = Number(tracker.quantityOrdered);
                                if (
                                  Number.isFinite(cpo) &&
                                  Number.isFinite(spo) &&
                                  Number.isFinite(qty) &&
                                  qty !== 0
                                ) {
                                  return formatTrackerAmount((cpo - spo) * qty);
                                }
                                return formatTrackerAmount(tracker.total);
                              })()}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              <span
                                className="fwt_600"
                                style={{
                                  color: getPoStatusColor(tracker.poStatus),
                                }}
                              >
                                {tracker.poStatus || "-"}
                              </span>
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {tracker.updatedBy?.name ||
                                tracker.createdBy?.name ||
                                "-"}
                            </td>
                            <td style={{ fontSize: "12px" }}>
                              {tracker.updatedAt
                                ? new Date(
                                    tracker.updatedAt,
                                  ).toLocaleDateString()
                                : "-"}
                            </td>
                            <td style={{ fontSize: "13px" }}>
                              <div className="d-flex justify-content-center align-items-center">
                                {canView && (
                                  <Link
                                    to={`/trackerView/${tracker._id}`}
                                    className="crsr_pntr mr-2"
                                    title="View"
                                  >
                                    <i className="fa-solid fa-eye" />
                                  </Link>
                                )}
                                {canUpdate && (
                                  <Link
                                    to={`/trackerEdit/${tracker._id}`}
                                    className="crsr_pntr"
                                    title="Edit"
                                  >
                                    <i className="fa-solid fa-pen" />
                                  </Link>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {!isLoading && trackers.length > 0 && (
                    <div className="supply-pagination-container">
                      <div className="pagination-wrapper">
                        {/* Entries Section */}
                        <div className="pagination-info">
                          <label
                            className="mb-0 mr-2"
                            style={{ fontSize: "13px" }}
                          >
                            Show:
                          </label>

                          <select
                            className="form-control form-control-sm"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                          >
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>

                          <span className="ml-2" style={{ fontSize: "13px" }}>
                            entries
                          </span>

                          <span
                            className="ml-3 text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            {startItem}–{endItem} of {totalCount}
                          </span>
                        </div>

                        {/* Pagination Buttons */}
                        <nav className="pagination-nav">
                          <ul className="pagination mb-0">
                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(1)}
                                disabled={currentPage === 1}
                                title="First Page"
                              >
                                <i className="fa-solid fa-angle-double-left"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage - 1)
                                }
                                disabled={currentPage === 1}
                                title="Previous Page"
                              >
                                <i className="fa-solid fa-angle-left"></i>
                              </button>
                            </li>

                            {getPageNumbers().map((page, index) =>
                              page === "ellipsis" ? (
                                <li
                                  key={`ellipsis-${index}`}
                                  className="page-item disabled"
                                >
                                  <span className="page-link">...</span>
                                </li>
                              ) : (
                                <li
                                  key={page}
                                  className={`page-item ${
                                    currentPage === page ? "active" : ""
                                  }`}
                                >
                                  <button
                                    className="page-link"
                                    onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </button>
                                </li>
                              ),
                            )}

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() =>
                                  handlePageChange(currentPage + 1)
                                }
                                disabled={currentPage === totalPages}
                                title="Next Page"
                              >
                                <i className="fa-solid fa-angle-right"></i>
                              </button>
                            </li>

                            <li
                              className={`page-item ${
                                currentPage === totalPages ? "disabled" : ""
                              }`}
                            >
                              <button
                                className="page-link"
                                onClick={() => handlePageChange(totalPages)}
                                disabled={currentPage === totalPages}
                                title="Last Page"
                              >
                                <i className="fa-solid fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div
        className={`modal fade ${showExportModal ? "show" : ""}`}
        style={{ display: showExportModal ? "block" : "none" }}
        tabIndex={-1}
        aria-labelledby="modalExportTrackerLabel"
        aria-hidden={!showExportModal}
        onClick={() => {
          if (!isExporting) setShowExportModal(false);
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="modalExportTrackerLabel">
                Export Customer Orders
              </h5>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={() => {
                  if (!isExporting) setShowExportModal(false);
                }}
                disabled={isExporting}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </div>
            <div className="modal-body">
              {(activeSearchQuery || anyMultiFilterActive) && (
                <p className="text-muted small mb-3">
                  Current filters will be included:
                  {activeSearchQuery && (
                    <span className="d-block">Search: {activeSearchQuery}</span>
                  )}
                  {activeMultiFilters.customer && (
                    <span className="d-block">
                      Customer:{" "}
                      {getFilterOptionLabel(
                        "customer",
                        activeMultiFilters.customer,
                      )}
                    </span>
                  )}
                  {activeMultiFilters.user && (
                    <span className="d-block">
                      User:{" "}
                      {getFilterOptionLabel("user", activeMultiFilters.user)}
                    </span>
                  )}
                  {activeMultiFilters.status && (
                    <span className="d-block">
                      PO Status: {activeMultiFilters.status}
                    </span>
                  )}
                  {(activeMultiFilters.fromDate ||
                    activeMultiFilters.toDate) && (
                    <span className="d-block">
                      Date:{" "}
                      {activeMultiFilters.fromDate
                        ? toApiDDMMYYYY(activeMultiFilters.fromDate)
                        : "-"}{" "}
                      to{" "}
                      {activeMultiFilters.toDate
                        ? toApiDDMMYYYY(activeMultiFilters.toDate)
                        : "-"}
                    </span>
                  )}
                </p>
              )}
              <div className="form-group mb-3">
                <label className="mb-2 d-block">Export Type</label>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportAllTrackers"
                    name="exportTypeTrackers"
                    className="custom-control-input"
                    value="all"
                    checked={exportMode === "all"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="exportAllTrackers"
                  >
                    All
                  </label>
                </div>
                <div className="custom-control custom-radio custom-control-inline">
                  <input
                    type="radio"
                    id="exportDateTrackers"
                    name="exportTypeTrackers"
                    className="custom-control-input"
                    value="date"
                    checked={exportMode === "date"}
                    onChange={(e) => setExportMode(e.target.value)}
                    disabled={isExporting}
                  />
                  <label
                    className="custom-control-label"
                    htmlFor="exportDateTrackers"
                  >
                    Filter by date
                  </label>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="mb-1">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportFromDate}
                    onChange={(e) => setExportFromDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                    max={exportToDate || undefined}
                  />
                </div>
                <div className="form-group col-md-6 mb-0">
                  <label className="mb-1">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={exportToDate}
                    onChange={(e) => setExportToDate(e.target.value)}
                    disabled={isExporting || exportMode !== "date"}
                    min={exportFromDate || undefined}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowExportModal(false)}
                disabled={isExporting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-blue"
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? "Exporting..." : "Export"}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showExportModal && <div className="modal-backdrop fade show" />}
    </div>
  );
};

export default TrackerUpdate;